import { BRAND, MEDIA, FOUNDERS } from "../lib/bakeryData";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Croissant, Users, Check } from "lucide-react";

const About = () => {
    const stats = [
        { icon: Croissant, label: "Bakes a day", value: "1000+" },
        { icon: Users, label: "Founders", value: "2" },
        { icon: Heart, label: "Branches", value: "3" },
    ];

    return (
        <main data-testid="about-page" className="bg-[#FAF8F5] pt-32 pb-20">
            {/* Intro */}
            <section className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <p className="font-handwriting text-3xl text-[#C5832B]">
                            Our story
                        </p>
                        <h1 className="font-serif text-5xl md:text-6xl text-[#3E2723] mt-3 leading-[1.05]">
                            Two boys, one oven, <br />
                            and a whole lot of <span className="italic">butter</span>.
                        </h1>
                        <p className="text-base md:text-lg text-[#6D4C41] mt-6 leading-relaxed max-w-2xl">
                            The Baker's Basket was opened on{" "}
                            <strong>24th May 2023</strong> in{" "}
                            <strong>Ambikapur</strong>. Two childhood friends —{" "}
                            <strong>Aaditya Budhia</strong> and{" "}
                            <strong>Aditya Singh</strong> — had spent more evenings than
                            they could count talking about croissants, sourdough, and the
                            kind of cake that makes a Tuesday feel like a Sunday.
                        </p>
                        <p className="text-base md:text-lg text-[#6D4C41] mt-4 leading-relaxed max-w-2xl">
                            They opened the doors with a double display case, one deck
                            oven, and the slightly insane idea that a small town deserved
                            world-class bread. Their neighbours seemed to agree. Today, the
                            Basket has three homes — Ambikapur, Surajpur, and Bishrampur —
                            and it still smells exactly the way it did on day one.
                        </p>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="relative">
                            <div className="aspect-[4/5] rounded-[2rem] overflow-hidden border border-[#E8D8C8] shadow-[0_30px_80px_-30px_rgba(62,39,35,0.35)]">
                                <img
                                    src={MEDIA.foundersPortrait}
                                    alt="Founders Aaditya Budhia & Aditya Singh"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -left-6 bg-white border border-[#E8D8C8] rounded-2xl px-5 py-3 shadow-lg">
                                <p className="font-handwriting text-2xl text-[#C5832B] leading-none">
                                    Est.
                                </p>
                                <p className="font-serif text-xl text-[#3E2723]">
                                    24 · 05 · 2023
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats strip */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 mt-24">
                <div className="grid sm:grid-cols-3 gap-5">
                    {stats.map((s) => (
                        <div
                            key={s.label}
                            data-testid={`about-stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
                            className="rounded-2xl border border-[#E8D8C8] bg-white p-8 text-center"
                        >
                            <s.icon className="w-6 h-6 text-[#C5832B] mx-auto" />
                            <p className="font-serif text-5xl text-[#3E2723] mt-3">
                                {s.value}
                            </p>
                            <p className="text-xs uppercase tracking-[0.18em] text-[#6D4C41] mt-2">
                                {s.label}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Founders */}
            <section
                data-testid="founders-section"
                className="max-w-7xl mx-auto px-6 md:px-10 mt-24 scroll-mt-28"
                id="founders"
            >
                <div className="text-center max-w-2xl mx-auto">
                    <p className="font-handwriting text-3xl text-[#C5832B]">
                        The faces behind the flour
                    </p>
                    <h2 className="font-serif text-4xl md:text-5xl text-[#3E2723] mt-3 leading-tight">
                        Meet the founders
                    </h2>
                    <p className="text-base text-[#6D4C41] mt-5 leading-relaxed">
                        Two childhood friends turned co-founders — one runs the engine
                        room, the other runs the counter. Together, they run the bakery.
                    </p>
                </div>

                <div className="mt-14 grid md:grid-cols-2 gap-8">
                    {FOUNDERS.map((f, idx) => (
                        <article
                            key={f.id}
                            data-testid={`founder-card-${f.id}`}
                            className="group bg-white border border-[#E8D8C8] rounded-3xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(62,39,35,0.35)] transition-all duration-500"
                        >
                            <div className="aspect-[4/5] overflow-hidden bg-[#F3EBE1] relative">
                                <img
                                    src={f.photo}
                                    alt={f.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                {f.isPlaceholderPhoto && (
                                    <span className="absolute top-4 right-4 px-3 py-1 rounded-full bg-[#3E2723]/85 text-[#FAF8F5] text-[10px] uppercase tracking-[0.18em] backdrop-blur-md">
                                        Photo coming soon
                                    </span>
                                )}
                                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#2A1A17]/85 via-[#2A1A17]/35 to-transparent">
                                    <p className="font-handwriting text-2xl text-[#C5832B] leading-none">
                                        0{idx + 1} · {f.role}
                                    </p>
                                    <h3 className="font-serif text-3xl text-[#FAF8F5] mt-2">
                                        {f.name}
                                    </h3>
                                </div>
                            </div>
                            <div className="p-7 md:p-8">
                                <p className="text-xs uppercase tracking-[0.18em] text-[#C5832B]">
                                    {f.focus}
                                </p>
                                <p className="text-sm md:text-base text-[#6D4C41] mt-4 leading-relaxed">
                                    {f.bio}
                                </p>
                                <ul className="mt-5 space-y-2.5">
                                    {f.highlights.map((h) => (
                                        <li
                                            key={h}
                                            className="flex items-start gap-2.5 text-sm text-[#3E2723]"
                                        >
                                            <span className="grid place-items-center w-5 h-5 rounded-full bg-[#C5832B]/15 text-[#C5832B] flex-shrink-0 mt-0.5">
                                                <Check className="w-3 h-3" />
                                            </span>
                                            {h}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {/* Values */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 mt-24">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className="aspect-[5/4] rounded-3xl overflow-hidden border border-[#E8D8C8]">
                        <img
                            src={MEDIA.breadDisplay}
                            alt="Fresh breads"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div>
                        <p className="font-handwriting text-3xl text-[#C5832B]">
                            What we believe
                        </p>
                        <h2 className="font-serif text-4xl md:text-5xl text-[#3E2723] mt-3 leading-tight">
                            Slow is the secret ingredient.
                        </h2>
                        <ul className="mt-8 space-y-6">
                            <li>
                                <h3 className="font-serif text-2xl text-[#3E2723]">
                                    Real ingredients only
                                </h3>
                                <p className="text-sm text-[#6D4C41] mt-1.5 leading-relaxed">
                                    French butter, Indian wheat, fresh eggs and Belgian
                                    chocolate. If we wouldn't feed it to our families, it
                                    doesn't make it to the counter.
                                </p>
                            </li>
                            <li>
                                <h3 className="font-serif text-2xl text-[#3E2723]">
                                    Small batches, every morning
                                </h3>
                                <p className="text-sm text-[#6D4C41] mt-1.5 leading-relaxed">
                                    Nothing sits around. Everything you taste was kneaded,
                                    shaped, and baked the same day you bought it.
                                </p>
                            </li>
                            <li>
                                <h3 className="font-serif text-2xl text-[#3E2723]">
                                    A bakery for the neighbourhood
                                </h3>
                                <p className="text-sm text-[#6D4C41] mt-1.5 leading-relaxed">
                                    We know our regulars by name — and the order they always
                                    place. That's the part we're proudest of.
                                </p>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Timeline */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 mt-24">
                <div className="text-center max-w-2xl mx-auto">
                    <p className="font-handwriting text-3xl text-[#C5832B]">
                        How it grew
                    </p>
                    <h2 className="font-serif text-4xl md:text-5xl text-[#3E2723] mt-3">
                        A short timeline
                    </h2>
                </div>
                <ol className="mt-14 relative border-l-2 border-[#E8D8C8] ml-4 md:ml-0 md:max-w-3xl md:mx-auto pl-8 space-y-10">
                    {[
                        {
                            date: "24 May 2023",
                            title: "The first oven, in Ambikapur",
                            text: "Aaditya & Aditya open The Baker's Basket — one deck oven, one double display case, and one dream.",
                        },
                        {
                            date: "2 March 2025",
                            title: "Surajpur joins the family",
                            text: "The second branch opens its doors on Nawapara Road — same recipes, same warmth, a brand new neighbourhood.",
                        },
                        {
                            date: "4 September 2025",
                            title: "Bishrampur, our third home",
                            text: "Coffee, cakes and calm arrive in Bishrampur. Three towns, one bakery family.",
                        },
                        {
                            date: "Today",
                            title: "Still baking at sunrise",
                            text: "The ovens light up before the sun does. More homes, more croissants, same obsession.",
                        },
                    ].map((step, i) => (
                        <li key={i} className="relative">
                            <span className="absolute -left-[42px] top-1.5 w-4 h-4 rounded-full bg-[#C5832B] border-4 border-[#FAF8F5]" />
                            <p className="text-xs uppercase tracking-[0.18em] text-[#C5832B]">
                                {step.date}
                            </p>
                            <h3 className="font-serif text-2xl text-[#3E2723] mt-1">
                                {step.title}
                            </h3>
                            <p className="text-sm text-[#6D4C41] mt-2 leading-relaxed max-w-xl">
                                {step.text}
                            </p>
                        </li>
                    ))}
                </ol>
            </section>

            {/* CTA */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 mt-24">
                <div className="rounded-[2rem] bg-[#2A1A17] text-[#F3EBE1] p-10 md:p-16 text-center">
                    <p className="font-handwriting text-3xl text-[#C5832B]">
                        Come say hi
                    </p>
                    <h2 className="font-serif text-4xl md:text-5xl mt-3">
                        We'll save you the corner table.
                    </h2>
                    <Link
                        to="/contact"
                        data-testid="about-contact-cta"
                        className="inline-flex items-center gap-2 mt-8 px-7 py-3.5 rounded-full bg-[#C5832B] text-[#2A1A17] text-sm hover:bg-[#FAF8F5] transition-colors"
                    >
                        Get in touch
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </main>
    );
};

export default About;
